import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { X, TrendingUp, BookOpen, Send, Award, Flame, MessageSquare } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;

function ActivityGraph({ activity }) {
  if (!activity || activity.length === 0) return (
    <p className="text-xs text-[#8B8794] text-center py-4">Belum ada aktivitas 30 hari terakhir</p>
  );
  const max = Math.max(...activity.map(a => a.count), 1);
  // build last 30 days grid
  const days = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const found = activity.find(a => a.date === key);
    days.push({ date: key, count: found ? found.count : 0 });
  }
  return (
    <div className="flex gap-1 flex-wrap">
      {days.map(({ date, count }) => {
        const intensity = count === 0 ? 0 : Math.ceil((count / max) * 4);
        const colors = ["bg-[#E8E5EE]", "bg-[#FFD8A8]", "bg-[#FFB347]", "bg-[#FF8C00]", "bg-[#CC6E00]"];
        return (
          <div key={date} title={`${date}: ${count} tugas`}
            className={`w-5 h-5 rounded-sm ${colors[intensity]} transition-colors`} />
        );
      })}
    </div>
  );
}

function ChatPanel({ userId, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/students/${userId}/chat`);
      setMessages(data);
    } catch {}
  };

  useEffect(() => { load(); }, [userId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/admin/students/${userId}/chat`, { content: text.trim() });
      setText("");
      load();
    } catch {} finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF9FC] rounded-xl min-h-[200px] max-h-[340px]">
        {messages.length === 0 && (
          <p className="text-[#8B8794] text-sm text-center py-8">Belum ada diskusi. Mulai percakapan dengan siswa ini.</p>
        )}
        {messages.map(m => {
          const isMe = m.sender_id === currentUser?.user_id;
          return (
            <div key={m.chat_id} className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
              {m.sender_picture ? (
                <img src={toAbsUrl(m.sender_picture)} className="w-8 h-8 rounded-full flex-shrink-0 object-cover" alt="" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#FF8C00] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {m.sender_name?.[0] || "?"}
                </div>
              )}
              <div className={`max-w-[75%] ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                <span className="text-[10px] text-[#8B8794] mb-0.5">{m.sender_name} · {new Date(m.created_at).toLocaleString("id-ID")}</span>
                <div className={`px-3 py-2 rounded-2xl text-sm ${isMe ? "bg-[#FF8C00] text-white rounded-tr-sm" : "bg-white text-[#1A1422] rounded-tl-sm border border-[#E8E5EE]"}`}>
                  {m.content}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder="Tulis pesan ke siswa..."
          className="input-field flex-1 text-sm"
        />
        <button onClick={send} disabled={sending || !text.trim()} className="btn-primary px-4">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function StudentPerformance({ userId, currentUser, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("performance");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/students/${userId}/performance`)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="card-surface w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E8E5EE]">
          {data && (
            <div className="flex items-center gap-3">
              {data.user.picture ? (
                <img src={toAbsUrl(data.user.picture)} className="w-12 h-12 rounded-full object-cover" alt="" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#F4F2F8] flex items-center justify-center text-[#FF8C00] font-bold text-xl">
                  {data.user.name?.[0] || "?"}
                </div>
              )}
              <div>
                <h2 className="font-heading font-bold text-[#1A1422] text-lg">{data.user.name}</h2>
                <p className="text-sm text-[#5C5868]">{data.user.email}</p>
              </div>
            </div>
          )}
          {loading && <p className="text-[#5C5868]">Memuat...</p>}
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F4F2F8]"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E8E5EE]">
          {[{ id: "performance", label: "Performa", icon: TrendingUp }, { id: "chat", label: "Diskusi", icon: MessageSquare }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? "border-[#FF8C00] text-[#FF8C00]" : "border-transparent text-[#5C5868] hover:text-[#1A1422]"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === "performance" && data && (
            <div className="space-y-6">
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#FFF4E6] rounded-xl p-4 text-center">
                  <Flame className="w-6 h-6 text-[#FF8C00] mx-auto mb-1" />
                  <div className="text-2xl font-bold text-[#FF8C00]">{data.streak_days}</div>
                  <div className="text-xs text-[#5C5868]">hari streak</div>
                </div>
                <div className="bg-[#F0FDF4] rounded-xl p-4 text-center">
                  <Award className="w-6 h-6 text-[#10B981] mx-auto mb-1" />
                  <div className="text-2xl font-bold text-[#10B981]">{data.overall_avg_grade ?? "–"}</div>
                  <div className="text-xs text-[#5C5868]">rata-rata nilai</div>
                </div>
                <div className="bg-[#F5F3FF] rounded-xl p-4 text-center">
                  <BookOpen className="w-6 h-6 text-[#6B21A8] mx-auto mb-1" />
                  <div className="text-2xl font-bold text-[#6B21A8]">{data.total_submissions}</div>
                  <div className="text-xs text-[#5C5868]">tugas terkirim</div>
                </div>
              </div>

              {/* Activity graph */}
              <div>
                <h3 className="text-sm font-semibold text-[#1A1422] mb-3">Aktivitas 30 hari terakhir</h3>
                <ActivityGraph activity={data.activity} />
                <p className="text-xs text-[#8B8794] mt-2">Setiap kotak = 1 hari. Semakin gelap = semakin banyak tugas dikirim.</p>
              </div>

              {/* Course progress */}
              <div>
                <h3 className="text-sm font-semibold text-[#1A1422] mb-3">Progress Kursus</h3>
                {data.course_progress.length === 0 && (
                  <p className="text-sm text-[#8B8794]">Belum terdaftar di kursus apapun.</p>
                )}
                <div className="space-y-3">
                  {data.course_progress.map(c => (
                    <div key={c.course_id} className="border border-[#E8E5EE] rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        {c.thumbnail && <img src={toAbsUrl(c.thumbnail)} className="w-10 h-10 rounded-lg object-cover" alt="" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#1A1422] text-sm truncate">{c.course_title}</p>
                          <p className="text-xs text-[#5C5868]">{c.completed_lessons}/{c.total_lessons} materi · {c.submissions_count} tugas · nilai: {c.avg_grade ?? "–"}</p>
                        </div>
                        <span className="text-sm font-bold text-[#FF8C00]">{c.progress_percent}%</span>
                      </div>
                      <div className="w-full bg-[#E8E5EE] rounded-full h-2">
                        <div className="bg-[#FF8C00] h-2 rounded-full transition-all" style={{ width: `${c.progress_percent}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "chat" && (
            <ChatPanel userId={userId} currentUser={currentUser} />
          )}
        </div>
      </div>
    </div>
  );
}
